import { CumpleCell, cumpleText } from './CumpleCell';
import { useCallback, useEffect, useState } from 'react';
import { Button, Input, Select, Textarea, useToast, Modal, Badge, DataTable, Column, EmptyState, LongText, IpText, NameCell } from '../../../shared/components';
import { getTurnoGeoRepository } from '../../../shared/services/turnoGeo/repositoryFactory';
import { getSettingsRepository } from '../../../shared/services/settings/repositoryFactory';
import { ApplicationMember } from '../../../shared/services/settings/types';
import { DECLARED_WORK_MODE_LABEL, OpenShift, RecordAuditEntry, RecordType, RECORD_TYPE_LABEL, TurnoGeoRecord, TurnoGeoSettings } from '../../../shared/services/turnoGeo/types';
import { InfoTip } from './InfoTip';
import { formatMinutes, shortDateTime, toDatetimeLocal } from '../jornadaUtil';

const repo = getTurnoGeoRepository();
const settingsRepo = getSettingsRepository();

const MODALITY_TONE: Record<string, 'success' | 'info' | 'warning'> = { PRESENCIAL: 'success', REMOTO: 'info', FUERA_DE_RANGO: 'warning' };
const AUDIT_LABEL: Record<RecordAuditEntry['action'], string> = {
  CORRECCION: 'Corrección de hora',
  CIERRE_TARDIO: 'Cierre tardío (empleado)',
  CIERRE_ADMIN: 'Cierre por administrador',
};
const LIMIT = 100;

const startOfDayIso = (d: string) => new Date(`${d}T00:00:00`).toISOString();
const endOfDayIso = (d: string) => new Date(`${d}T23:59:59.999`).toISOString();

/**
 * Configuración > Registros de marcación (solo administrador): todas las marcaciones con nombre,
 * documento, tipo, modalidad, sede, IP y la desviación frente al horario; filtros; corrección de la
 * hora de una marcación (queda la hora original, quién y por qué) y cierre de turnos olvidados.
 */
export function RecordsAdminTab() {
  const toast = useToast();
  const [members, setMembers] = useState<ApplicationMember[]>([]);
  const [settings, setSettings] = useState<TurnoGeoSettings | null>(null);
  const [rows, setRows] = useState<TurnoGeoRecord[] | null>(null);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState<OpenShift[]>([]);
  const [userId, setUserId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [recordType, setRecordType] = useState('');
  const [modalityCode, setModalityCode] = useState('');

  const [correcting, setCorrecting] = useState<TurnoGeoRecord | null>(null);
  const [newTime, setNewTime] = useState('');
  const [reason, setReason] = useState('');
  const [history, setHistory] = useState<{ record: TurnoGeoRecord; entries: RecordAuditEntry[] } | null>(null);
  const [closing, setClosing] = useState<OpenShift | null>(null);
  const [closeAt, setCloseAt] = useState('');
  const [closeNote, setCloseNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [photo, setPhoto] = useState<{ record: TurnoGeoRecord; url: string | null } | null>(null);

  const loadOpen = useCallback(() => {
    repo.listOpenShifts().then(setOpen).catch(() => setOpen([]));
  }, []);

  const [revalidating, setRevalidating] = useState(false);
  const revalidate = async () => {
    if (!dateFrom || !dateTo) { toast.show('Elige Desde y Hasta para revalidar un período.', 'error'); return; }
    if (!window.confirm('Esto recalcula la modalidad GUARDADA de los registros del período con las sedes y ubicaciones de hoy. ¿Continuar?')) return;
    setRevalidating(true);
    try {
      const r = await repo.revalidateRecords({ userId: userId || undefined, dateFrom: startOfDayIso(dateFrom), dateTo: endOfDayIso(dateTo) });
      toast.show(`Revisados ${r.reviewed}; actualizados ${r.changed}.`, 'success');
      search();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo revalidar.', 'error');
    } finally {
      setRevalidating(false);
    }
  };

  const search = useCallback(() => {
    setRows(null);
    repo
      .listRecords({
        userId: userId || undefined,
        dateFrom: dateFrom ? startOfDayIso(dateFrom) : undefined,
        dateTo: dateTo ? endOfDayIso(dateTo) : undefined,
        recordType: (recordType || undefined) as RecordType | undefined,
        modalityCode: modalityCode || undefined,
        page: 1,
        pageSize: LIMIT,
      })
      .then((r) => {
        setRows(r.items);
        setTotal(r.total);
      })
      .catch((err) => {
        setRows([]);
        toast.show(err instanceof Error ? err.message : 'No se pudieron cargar los registros.', 'error');
      });
  }, [userId, dateFrom, dateTo, recordType, modalityCode, toast]);

  useEffect(() => {
    settingsRepo.listApplicationMembers('turno-geo', true).then((m) => setMembers(m.filter((x) => x.hasAccess)));
    repo.getConfiguration().then(setSettings);
    loadOpen();
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCorrect = (r: TurnoGeoRecord) => {
    setCorrecting(r);
    setNewTime(toDatetimeLocal(r.recordTimestamp));
    setReason('');
  };
  const saveCorrection = async () => {
    if (!correcting) return;
    setIsSaving(true);
    try {
      await repo.correctRecord(correcting.id, { recordTimestamp: new Date(newTime).toISOString(), reason });
      toast.show('Hora corregida. Quedó el historial del cambio.');
      setCorrecting(null);
      search();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo corregir.', 'error');
    } finally {
      setIsSaving(false);
    }
  };
  const openPhoto = async (r: TurnoGeoRecord) => {
    setPhoto({ record: r, url: null });
    try {
      setPhoto({ record: r, url: await repo.getRecordPhotoUrl(r.id) });
    } catch (err) {
      setPhoto(null);
      toast.show(err instanceof Error ? err.message : 'No se pudo cargar la foto.', 'error');
    }
  };
  const openHistory = async (r: TurnoGeoRecord) => {
    try {
      setHistory({ record: r, entries: await repo.listRecordAudit(r.id) });
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo cargar el historial.', 'error');
    }
  };
  const openClose = (o: OpenShift) => {
    setClosing(o);
    // La salida programada de ese día (ya ajustada por el servidor para que sea posterior a la última marcación).
    setCloseAt(toDatetimeLocal(o.suggestedCloseAt ? new Date(o.suggestedCloseAt) : new Date()));
    setCloseNote('');
  };
  const closingLast = closing?.lastMarkAt ? new Date(closing.lastMarkAt) : null;
  const closeError =
    closing && closeAt && closingLast && new Date(closeAt).getTime() <= closingLast.getTime()
      ? `Debe ser posterior a la última marcación del turno (${RECORD_TYPE_LABEL[closing.lastRecordType].toLowerCase()}: ${shortDateTime(closingLast.toISOString())}).`
      : null;
  const saveClose = async () => {
    if (!closing) return;
    setIsSaving(true);
    try {
      await repo.adminCloseShift({ userId: closing.userId, closeAt: new Date(closeAt).toISOString(), note: closeNote });
      toast.show('Turno cerrado.');
      setClosing(null);
      loadOpen();
      search();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo cerrar el turno.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const driftLimit = (settings?.clockDriftMinutes ?? 3) * 60;

  const columns: Column<TurnoGeoRecord>[] = [
    {
      key: 'fecha', filterValue: (r) => r.recordTimestamp.slice(0, 10),
      header: 'Fecha y hora',
      sortable: true,
      sortValue: (r) => r.recordTimestamp,
      render: (r) => (
        <div className="whitespace-nowrap">
          <p className="font-medium text-ink-800">{shortDateTime(r.recordTimestamp)}</p>
          <div className="mt-0.5 flex gap-1">
            {r.isLateClose && <Badge tone="warning">Cierre tardío</Badge>}
            {r.isCorrected && <Badge tone="info">Corregida</Badge>}
          </div>
        </div>
      ),
    },
    {
      key: 'empleado',
      header: 'Empleado',
      sortable: true,
      sortValue: (r) => r.userFullName,
      render: (r) => <NameCell name={r.userFullName} />,
    },
    { key: 'tipo', filterValue: (r) => RECORD_TYPE_LABEL[r.recordType], header: 'Marcación', render: (r) => <Badge tone={r.recordType === 'ENTRADA' || r.recordType === 'ENTRADA_ALMUERZO' ? 'info' : 'neutral'}>{RECORD_TYPE_LABEL[r.recordType]}</Badge> },
    { key: 'modalidad', filterValue: (r) => r.modalityName, header: 'Modalidad', render: (r) => <Badge tone={MODALITY_TONE[r.modalityCode] ?? 'neutral'}>{r.modalityName}</Badge> },
    {
      key: 'declaro',
      header: 'Declaró',
      render: (r) => {
        if (!r.declaredWorkMode) return <span className="text-ink-300">—</span>;
        const mismatch = r.declaredWorkMode === 'OFICINA' ? r.modalityCode !== 'PRESENCIAL' : r.modalityCode === 'PRESENCIAL';
        return (
          <div className="whitespace-nowrap">
            <span className="text-xs font-medium text-ink-700">{DECLARED_WORK_MODE_LABEL[r.declaredWorkMode]}</span>
            {mismatch && <div><Badge tone="warning">No coincide</Badge></div>}
          </div>
        );
      },
    },
    { key: 'sede', filterValue: (r) => r.matchedOfficeNetworkName ?? '', header: 'Sede / lugar', render: (r) => r.matchedOfficeNetworkName ?? (r.matchedHomeLocationId ? 'Ubicación propia' : '—') },
    { key: 'ip', header: 'IP', render: (r) => <IpText ip={r.detectedPublicIp} expandable /> },
    {
      key: 'desv',
      header: 'Vs. horario',
      render: (r) =>
        r.scheduleDeviationMinutes === null ? (
          '—'
        ) : (
          <span className="whitespace-nowrap">
            {r.scheduleDeviationMinutes === 0 ? 'Puntual' : `${r.scheduleDeviationMinutes > 0 ? '+' : '−'}${formatMinutes(r.scheduleDeviationMinutes)}`}
            {r.deviationConfirmed && <span className="ml-1 text-[10px] text-ink-400">(confirmó)</span>}
          </span>
        ),
    },
    {
      key: 'reloj',
      header: 'Reloj celular',
      render: (r) =>
        r.clockDriftSeconds === null ? (
          '—'
        ) : Math.abs(r.clockDriftSeconds) > driftLimit ? (
          <Badge tone="warning">{r.clockDriftSeconds > 0 ? '+' : '−'}{formatMinutes(r.clockDriftSeconds / 60)}</Badge>
        ) : (
          <span className="text-ink-400">OK</span>
        ),
    },
    {
      key: 'acc',
      header: '',
      render: (r) => (
        <div className="flex gap-2">
          {r.photoUrl && <Button variant="outline" size="md" onClick={() => openPhoto(r)}>Foto</Button>}
          <Button variant="outline" size="md" onClick={() => openCorrect(r)}>Corregir</Button>
          {(r.isCorrected || r.isLateClose) && (
            <Button variant="ghost" size="md" onClick={() => openHistory(r)}>Historial</Button>
          )}
        </div>
      ),
    },
    { key: 'cumple', filterValue: (r) => cumpleText(r), header: 'Cumple', render: (r) => <CumpleCell r={r} /> },
    {
      key: 'nota',
      header: 'Nota / motivo',
      render: (r) => (
        <div className="max-w-[260px] text-xs text-ink-600">
          {r.note && <p><strong>Nota:</strong> <LongText text={r.note} max={60} /></p>}
          {r.modalityReason && <p className="text-ink-400"><LongText text={r.modalityReason} max={70} /></p>}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Turnos abiertos */}
      <div className="rounded-xl border border-ink-100 bg-surface p-4">
        <p className="flex items-center gap-2 text-sm font-semibold text-ink-800">
          Turnos abiertos ahora ({open.length})
          <InfoTip title="Turnos abiertos" text="Personas que marcaron entrada y todavía no marcan la salida. Los que superan el máximo de horas (Parámetros > Horas máximas de un turno abierto) aparecen en rojo: probablemente olvidaron cerrar. Puedes cerrarles el turno indicando la hora real y el motivo; queda el historial." />
        </p>
        {open.length === 0 ? (
          <p className="mt-2 text-xs text-ink-400">No hay turnos abiertos.</p>
        ) : (
          <ul className="mt-3 divide-y divide-ink-100">
            {open.map((o) => (
              <li key={o.userId} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <div>
                  <p className="text-sm font-medium text-ink-800">
                    {o.userFullName} {o.stale && <Badge tone="critical">Posible olvido de cierre</Badge>}
                  </p>
                  <p className="text-xs text-ink-400">
                    {o.userDocumentNumber ? `CC ${o.userDocumentNumber} · ` : ''}Entró {shortDateTime(o.entryAt)} · lleva {formatMinutes(o.ageMinutes)} · última marcación: {RECORD_TYPE_LABEL[o.lastRecordType].toLowerCase()}
                    {o.blockReason === 'NUEVA_JORNADA' ? ' · ya empezó otra jornada' : ''}
                  </p>
                </div>
                <Button variant={o.stale ? 'danger' : 'outline'} size="md" onClick={() => openClose(o)}>Cerrar turno</Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Filtros */}
      <div className="grid gap-3 rounded-xl border border-ink-100 bg-surface p-4 sm:grid-cols-2 lg:grid-cols-5">
        <Select label="Empleado" value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="Todos" options={members.map((m) => ({ value: m.id, label: m.fullName }))} />
        <Input label="Desde" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        <Input label="Hasta" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        <Select
          label="Marcación"
          value={recordType}
          onChange={(e) => setRecordType(e.target.value)}
          placeholder="Todas"
          options={(Object.keys(RECORD_TYPE_LABEL) as RecordType[]).map((t) => ({ value: t, label: RECORD_TYPE_LABEL[t] }))}
        />
        <Select
          label="Modalidad"
          value={modalityCode}
          onChange={(e) => setModalityCode(e.target.value)}
          placeholder="Todas"
          options={[{ value: 'PRESENCIAL', label: 'Presencial' }, { value: 'REMOTO', label: 'Remoto' }, { value: 'FUERA_DE_RANGO', label: 'Fuera de rango' }, { value: 'PENDIENTE_APROBACION', label: 'Pendiente de aprobación' }]}
        />
        <div className="sm:col-span-2 lg:col-span-5">
          <Button size="md" onClick={search}>Buscar</Button>
          <Button className="ml-2" variant="outline" size="md" disabled={revalidating} onClick={revalidate}>{revalidating ? 'Revalidando…' : 'Revalidar período'}</Button>
          <span className="ml-3 text-xs text-ink-400">
            {rows ? (total > LIMIT ? `Mostrando los ${LIMIT} más recientes de ${total}. Afina los filtros para ver el resto.` : `${total} registro(s).`) : 'Cargando…'}
          </span>
        </div>
      </div>

      {rows && rows.length === 0 ? (
        <EmptyState icon="🕒" title="Sin registros" description="No hay marcaciones con esos filtros." />
      ) : (
        <DataTable excel fixedKey="empleado" columns={columns} rows={rows ?? []} rowKey={(r) => r.id} isLoading={rows === null} defaultSortKey="fecha" />
      )}

      {/* Corregir */}
      <Modal open={!!correcting} onClose={() => setCorrecting(null)} title="Corregir hora de la marcación">
        {correcting && (
          <div className="space-y-3">
            <div className="rounded-xl bg-ink-50 p-3 text-xs text-ink-600">
              <p><strong>{correcting.userFullName}</strong> · {RECORD_TYPE_LABEL[correcting.recordType]}</p>
              <p>Hora actual: {shortDateTime(correcting.recordTimestamp)}{correcting.originalTimestamp ? ` · original: ${shortDateTime(correcting.originalTimestamp)}` : ''}</p>
            </div>
            <Input label="Nueva fecha y hora" type="datetime-local" value={newTime} max={toDatetimeLocal(new Date())} onChange={(e) => setNewTime(e.target.value)} />
            <Textarea label="Motivo de la corrección" value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} hint="Obligatorio (mínimo 5 caracteres). Queda en el historial junto con la hora original y tu nombre." />
            <div className="flex gap-3 pt-1">
              <Button variant="outline" fullWidth onClick={() => setCorrecting(null)}>Cancelar</Button>
              <Button fullWidth onClick={saveCorrection} isLoading={isSaving} disabled={!newTime || reason.trim().length < 5}>Guardar corrección</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Foto de la marcación (anti-suplantación) */}
      <Modal open={!!photo} onClose={() => setPhoto(null)} title="Foto de la marcación">
        {photo && (
          <div className="space-y-3">
            <p className="text-xs text-ink-500">{photo.record.userFullName} · {RECORD_TYPE_LABEL[photo.record.recordType]} · {shortDateTime(photo.record.recordTimestamp)}</p>
            {photo.url ? <img src={photo.url} alt="Foto tomada al marcar" className="w-full rounded-xl border border-ink-100" /> : <p className="text-sm text-ink-400">Cargando foto…</p>}
            <Button variant="outline" fullWidth onClick={() => setPhoto(null)}>Cerrar</Button>
          </div>
        )}
      </Modal>

      {/* Historial */}
      <Modal open={!!history} onClose={() => setHistory(null)} title="Historial de la marcación">
        {history && (
          <div className="space-y-3">
            <p className="text-xs text-ink-500">
              {history.record.userFullName} · {RECORD_TYPE_LABEL[history.record.recordType]} · hoy figura: {shortDateTime(history.record.recordTimestamp)}
              {history.record.isLateClose && <> · registrada en el sistema el {shortDateTime(history.record.serverTimestamp)}</>}
            </p>
            {history.entries.length === 0 ? (
              <p className="text-sm text-ink-400">Sin movimientos.</p>
            ) : (
              <ul className="space-y-3">
                {history.entries.map((e) => (
                  <li key={e.id} className="rounded-xl border border-ink-100 p-3 text-xs text-ink-600">
                    <p className="text-sm font-semibold text-ink-800">{AUDIT_LABEL[e.action]}</p>
                    <p>{e.oldTimestamp ? `${shortDateTime(e.oldTimestamp)} → ` : 'Hora indicada: '}{e.newTimestamp ? shortDateTime(e.newTimestamp) : '—'}</p>
                    <p className="mt-1"><strong>Motivo:</strong> {e.reason}</p>
                    <p className="text-ink-400">Por {e.changedBy} · {shortDateTime(e.changedDate)}</p>
                  </li>
                ))}
              </ul>
            )}
            <Button variant="outline" fullWidth onClick={() => setHistory(null)}>Cerrar</Button>
          </div>
        )}
      </Modal>

      {/* Cierre por el admin */}
      <Modal open={!!closing} onClose={() => setClosing(null)} title="Cerrar turno abierto">
        {closing && (
          <div className="space-y-3">
            <div className="rounded-xl bg-ink-50 p-3 text-xs text-ink-600">
              <p><strong>{closing.userFullName}</strong> entró el {shortDateTime(closing.entryAt)}.</p>
              <p>Última marcación: {RECORD_TYPE_LABEL[closing.lastRecordType].toLowerCase()} · {shortDateTime(closing.lastMarkAt ?? closing.lastRecordAt)}</p>
              {closing.scheduledEndAt && <p>Salida programada: {shortDateTime(closing.scheduledEndAt)}</p>}
            </div>
            <Input
              label="Hora real de salida"
              type="datetime-local"
              value={closeAt}
              min={closingLast ? toDatetimeLocal(new Date(Math.floor(closingLast.getTime() / 60_000) * 60_000 + 60_000)) : undefined}
              max={toDatetimeLocal(new Date())}
              onChange={(e) => setCloseAt(e.target.value)}
              error={closeError ?? undefined}
            />
            <Textarea label="Motivo del cierre" value={closeNote} maxLength={300} onChange={(e) => setCloseNote(e.target.value)} hint="Obligatorio (mínimo 5 caracteres). Queda como cierre tardío con tu nombre y en el histórico de turnos sin cerrar." />
            <div className="flex gap-3 pt-1">
              <Button variant="outline" fullWidth onClick={() => setClosing(null)}>Cancelar</Button>
              <Button fullWidth onClick={saveClose} isLoading={isSaving} disabled={!closeAt || closeNote.trim().length < 5 || !!closeError}>Cerrar turno</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
